/**
 * Fingerprint / Face biometric sign-in (native only).
 *
 * Runs ONLY inside the Capacitor native shell (Android/iOS). On the web
 * (browser, SSR, Lovable preview) every export is a guarded no-op, so the
 * website is completely unaffected — the biometric plugin is imported
 * dynamically inside each guard and never enters the web/SSR bundle.
 *
 * HOW IT WORKS
 * ------------
 *  1. After a normal email+password sign-in succeeds on a native device, we
 *     silently stash the Supabase REFRESH token in the OS secure store
 *     (Android Keystore / iOS Keychain) via `setCredentials` and flip an
 *     "enabled" flag in Preferences.
 *  2. On a later launch the login screen shows a "Sign in with fingerprint"
 *     button. Tapping it calls `getBiometricCredential`, which triggers the OS
 *     biometric prompt (fingerprint / Face ID). Only after a successful match
 *     does the secure store return the refresh token, which we exchange for a
 *     fresh session — no password typed.
 *
 * The refresh token never touches localStorage; it lives only in the
 * hardware-backed secure store, released solely on a verified biometric match.
 */

import { isNativeApp } from "./capacitor";

/** Secure-store namespace used as the keychain "server" key. */
const BIOMETRIC_SERVER = "digitallybaffled.com";
/** Preferences flag marking that the user opted into biometric sign-in. */
const ENABLED_KEY = "biometric_login_enabled";

export type BiometryKind = "fingerprint" | "face" | "iris" | "biometric" | "none";

/**
 * Why a biometric attempt failed, so the UI can choose the right fallback:
 *  - "cancelled"   → user dismissed the prompt; keep the button, let them retry.
 *  - "unavailable" → no sensor / not enrolled / hardware error / permission
 *                    denied; hide the button and steer the user to email + password.
 *  - "lockout"     → too many failed attempts; the OS locked the sensor.
 *  - "no-credential" → nothing stored for this device; fall back to password.
 *  - "failed"      → generic match failure; let them retry or use password.
 */
export type BiometricErrorReason =
  | "cancelled"
  | "unavailable"
  | "lockout"
  | "no-credential"
  | "failed";

/**
 * How long (ms) the biometric button stays disabled before it automatically
 * re-enables itself, keyed by the failure reason. After the cooldown elapses
 * the UI silently re-offers fingerprint / Face sign-in; email + password stays
 * usable the entire time as the fallback.
 *  - "lockout"   → the OS locked the sensor; give it a longer breather (30s).
 *  - "failed"    → a transient match glitch; a short pause (8s) then retry.
 * Other reasons don't get a cooldown: "cancelled" can be retried immediately,
 * while "unavailable" / "no-credential" hide the button entirely.
 */
export const BIOMETRIC_COOLDOWN_MS: Partial<Record<BiometricErrorReason, number>> = {
  lockout: 30_000,
  failed: 8_000,
};

/** Cooldown (ms) for a given failure reason, or 0 when retry is immediate. */
export function biometricCooldownMs(reason: BiometricErrorReason): number {
  return BIOMETRIC_COOLDOWN_MS[reason] ?? 0;
}

/** Error thrown by `getBiometricCredential` carrying a machine-readable reason. */
export class BiometricError extends Error {
  reason: BiometricErrorReason;
  constructor(reason: BiometricErrorReason, message: string) {
    super(message);
    this.name = "BiometricError";
    this.reason = reason;
  }
}

/**
 * Map the native plugin's raw error into a stable {@link BiometricErrorReason}.
 * The `@capgo/capacitor-native-biometric` plugin rejects with an error whose
 * `.code` is a numeric string; we also sniff the message as a fallback because
 * codes differ slightly across Android/iOS plugin versions.
 */
function classifyBiometricError(err: unknown): BiometricErrorReason {
  const code = String((err as { code?: string | number })?.code ?? "");
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();

  // User explicitly dismissed / cancelled the prompt.
  if (
    code === "10" ||
    code === "16" ||
    /cancel|canceled|cancelled|user.?fallback|dismiss/.test(msg)
  ) {
    return "cancelled";
  }
  // Sensor locked after too many failed attempts.
  if (code === "2" || code === "4" || /lockout|too many attempts|locked/.test(msg)) {
    return "lockout";
  }
  // Hardware missing, disabled, no fingerprints/face enrolled, or permission denied.
  if (
    code === "1" ||
    code === "3" ||
    /unavailable|not available|no hardware|not enrolled|no biometric|permission|denied|disabled/.test(
      msg,
    )
  ) {
    return "unavailable";
  }
  return "failed";
}

/** Whether the device hardware + enrolment supports biometric auth. */
export async function isBiometricAvailable(): Promise<{
  available: boolean;
  kind: BiometryKind;
}> {
  if (!(await isNativeApp())) return { available: false, kind: "none" };
  try {
    const { NativeBiometric, BiometryType } = await import(
      "@capgo/capacitor-native-biometric"
    );
    const result = await NativeBiometric.isAvailable({ useFallback: false });
    if (!result.isAvailable) return { available: false, kind: "none" };
    const kind: BiometryKind =
      result.biometryType === BiometryType.FACE_ID ||
      result.biometryType === BiometryType.FACE_AUTHENTICATION
        ? "face"
        : result.biometryType === BiometryType.TOUCH_ID ||
            result.biometryType === BiometryType.FINGERPRINT
          ? "fingerprint"
          : result.biometryType === BiometryType.IRIS_AUTHENTICATION
            ? "iris"
            : "biometric";
    return { available: true, kind };
  } catch {
    return { available: false, kind: "none" };
  }
}

/** True when the user has enabled biometric sign-in AND a token is stored. */
export async function hasBiometricLogin(): Promise<boolean> {
  if (!(await isNativeApp())) return false;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: ENABLED_KEY });
    return value === "true";
  } catch {
    return false;
  }
}

/**
 * Persist the refresh token in the secure store and mark biometric login as
 * enabled. Called silently right after a successful password sign-in on native.
 * Returns false (without throwing) if anything is unavailable.
 */
export async function enableBiometricLogin(
  email: string,
  refreshToken: string,
): Promise<boolean> {
  if (!email || !refreshToken) return false;
  if (!(await isBiometricAvailable()).available) return false;
  try {
    const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");
    const { Preferences } = await import("@capacitor/preferences");
    await NativeBiometric.setCredentials({
      username: email,
      password: refreshToken,
      server: BIOMETRIC_SERVER,
    });
    await Preferences.set({ key: ENABLED_KEY, value: "true" });
    return true;
  } catch {
    return false;
  }
}

/**
 * Trigger the OS biometric prompt and, on success, return the stored
 * credentials. Throws a {@link BiometricError} with a classified `reason` if
 * the sensor is unavailable, permission is denied, the user cancels, or the
 * match fails — so callers can pick the right fallback (retry vs. email login).
 */
export async function getBiometricCredential(): Promise<{
  email: string;
  refreshToken: string;
}> {
  if (!(await isNativeApp())) {
    throw new BiometricError(
      "unavailable",
      "Biometric sign-in is only available in the app.",
    );
  }

  let NativeBiometric;
  try {
    ({ NativeBiometric } = await import("@capgo/capacitor-native-biometric"));
  } catch {
    throw new BiometricError(
      "unavailable",
      "Biometric sign-in isn't available on this device. Please use your email and password.",
    );
  }

  // Re-check hardware/enrolment up front so a removed sensor or revoked
  // permission produces a clean "unavailable" rather than a cryptic failure.
  try {
    const status = await NativeBiometric.isAvailable({ useFallback: false });
    if (!status.isAvailable) {
      throw new BiometricError(
        "unavailable",
        "Fingerprint / Face sign-in isn't set up on this device. Please use your email and password.",
      );
    }
  } catch (e) {
    if (e instanceof BiometricError) throw e;
    throw new BiometricError(
      classifyBiometricError(e),
      "Fingerprint / Face sign-in isn't available right now. Please use your email and password.",
    );
  }

  // Prompt the user for their fingerprint / face. Rejects on cancel/fail.
  try {
    await NativeBiometric.verifyIdentity({
      reason: "Sign in to Digitally Baffled",
      title: "Biometric sign-in",
      subtitle: "Confirm it's you",
      description: "Use your fingerprint or face to continue.",
    });
  } catch (e) {
    const reason = classifyBiometricError(e);
    const message =
      reason === "cancelled"
        ? "Biometric sign-in was cancelled. You can try again or sign in with your password."
        : reason === "lockout"
          ? "Too many attempts — biometric sign-in is locked. Please sign in with your password."
          : reason === "unavailable"
            ? "Fingerprint / Face sign-in isn't available right now. Please use your email and password."
            : "Couldn't verify your fingerprint / face. Please try again or use your password.";
    throw new BiometricError(reason, message);
  }

  const creds = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
  if (!creds?.password) {
    throw new BiometricError(
      "no-credential",
      "No saved sign-in found on this device. Please sign in with your password.",
    );
  }
  return { email: creds.username, refreshToken: creds.password };
}

/** Remove the stored token and clear the enabled flag (e.g. on sign-out). */
export async function disableBiometricLogin(): Promise<void> {
  if (!(await isNativeApp())) return;
  try {
    const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");
    const { Preferences } = await import("@capacitor/preferences");
    try {
      await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
    } catch {
      /* nothing stored — ignore. */
    }
    await Preferences.remove({ key: ENABLED_KEY });
  } catch {
    /* plugins unavailable — ignore. */
  }
}
